import React, { useState, useCallback, useEffect } from 'react';
import {
  Shield, Users, Plus, Trash2, RefreshCw, Key, Lock,
  AlertTriangle, UserPlus, Settings, Power, PowerOff,
  Gauge, Loader2, CheckCircle2, XCircle, ShieldCheck,
  Eye, EyeOff, Copy, AlertCircle, Rocket
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Alert, AlertDescription } from './ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import apiService from '../services/api';

const ACLManager = ({ clusterId, credentials }) => {
  const [activeTab, setActiveTab] = useState('security');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // ACL Config State
  const [aclStatus, setAclStatus] = useState(null);
  const [aclStatusLoading, setAclStatusLoading] = useState(false);
  const [showEnableDialog, setShowEnableDialog] = useState(false);
  const [enableOptions, setEnableOptions] = useState({
    superUsers: '',
    allowEveryoneIfNoAcl: true,
    restartKafka: true
  });
  const [enableLogs, setEnableLogs] = useState([]);
  const [enabling, setEnabling] = useState(false);
  
  // ACL State
  const [acls, setAcls] = useState([]);
  const [showAddACL, setShowAddACL] = useState(false);
  const [newACL, setNewACL] = useState({
    principal: 'User:',
    operation: 'All',
    resource: '',
    resourceType: 'topic',
    resourcePattern: 'literal',
    host: '*'
  });
  
  // User State
  const [users, setUsers] = useState([]);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    mechanism: 'SCRAM-SHA-512'
  });
  
  // Quota State
  const [quotas, setQuotas] = useState([]);
  const [showAddQuota, setShowAddQuota] = useState(false);
  const [newQuota, setNewQuota] = useState({
    username: '',
    clientId: '',
    producerByteRate: '',
    consumerByteRate: '',
    requestPercentage: ''
  });

  // Production Security State
  const [securityStatus, setSecurityStatus] = useState(null);
  const [securityStatusLoading, setSecurityStatusLoading] = useState(false);
  const [showProductionSetup, setShowProductionSetup] = useState(false);
  const [productionSetupOptions, setProductionSetupOptions] = useState({
    adminUsername: 'superadmin',
    adminPassword: '',
    useGeneratedPassword: true,
    mechanism: 'SCRAM-SHA-512',
    allowEveryoneIfNoAcl: false,
    additionalSuperUsers: '',
    restartKafka: true
  });
  const [productionSetupLogs, setProductionSetupLogs] = useState([]);
  const [settingUpProduction, setSettingUpProduction] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showCredentialsDialog, setShowCredentialsDialog] = useState(false);
  const [manualCredentials, setManualCredentials] = useState({
    username: '',
    password: '',
    mechanism: 'SCRAM-SHA-512'
  });

  // Load ACL Status
  const loadAclStatus = useCallback(async () => {
    if (!credentials?.username) return;
    
    try {
      setAclStatusLoading(true);
      const res = await apiService.getACLStatus(clusterId, credentials);
      if (res.success) {
        setAclStatus(res);
      }
    } catch (err) {
      console.error('Failed to load ACL status:', err);
    } finally {
      setAclStatusLoading(false);
    }
  }, [clusterId, credentials]);

  const loadACLs = useCallback(async () => {
    if (!credentials?.username) return;
    
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.listACLs(clusterId, credentials);
      if (res.success) {
        setAcls(res.acls || []);
        // Check if ACL is not enabled
        if (res.aclNotEnabled) {
          setAclStatus(prev => ({ ...prev, enabled: false, notConfiguredOnBroker: true }));
        }
      } else {
        // Check if error is because ACL is not enabled
        if (res.aclNotEnabled || (res.message && res.message.includes('No Authorizer'))) {
          setAclStatus(prev => ({ ...prev, enabled: false, notConfiguredOnBroker: true }));
          setAcls([]);
        } else {
          setError(res.message || 'Failed to load ACLs');
        }
      }
    } catch (err) {
      // Check if error is because ACL is not enabled
      const errMsg = err.message || '';
      if (errMsg.includes('No Authorizer') || errMsg.includes('SecurityDisabled')) {
        setAclStatus(prev => ({ ...prev, enabled: false, notConfiguredOnBroker: true }));
        setAcls([]);
      } else {
        setError('Failed to load ACLs: ' + err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [clusterId, credentials]);

  const loadUsers = useCallback(async () => {
    if (!credentials?.username) return;
    
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.listUsers(clusterId, credentials);
      if (res.success) {
        setUsers(res.users || []);
      }
    } catch (err) {
      setError('Failed to load users: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [clusterId, credentials]);

  const loadQuotas = useCallback(async () => {
    if (!credentials?.username) return;
    
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.listQuotas(clusterId, credentials);
      if (res.success) {
        setQuotas(res.quotas || []);
      }
    } catch (err) {
      setError('Failed to load quotas: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [clusterId, credentials]);

  // Load Security Status
  const loadSecurityStatus = useCallback(async () => {
    try {
      setSecurityStatusLoading(true);
      const res = await apiService.getSecurityStatus(clusterId);
      if (res.success) {
        setSecurityStatus(res);
      }
    } catch (err) {
      console.error('Failed to load security status:', err);
    } finally {
      setSecurityStatusLoading(false);
    }
  }, [clusterId]);

  // Handle Production Security Setup
  const handleProductionSecuritySetup = async () => {
    setSettingUpProduction(true);
    setProductionSetupLogs([]);
    setGeneratedPassword(null);
    setError(null);
    
    try {
      const additionalUsers = productionSetupOptions.additionalSuperUsers
        .split(',')
        .map(u => u.trim())
        .filter(u => u);
      
      const res = await apiService.setupProductionSecurity(clusterId, credentials, {
        adminUsername: productionSetupOptions.adminUsername,
        adminPassword: productionSetupOptions.useGeneratedPassword ? undefined : productionSetupOptions.adminPassword,
        mechanism: productionSetupOptions.mechanism,
        allowEveryoneIfNoAcl: productionSetupOptions.allowEveryoneIfNoAcl,
        additionalSuperUsers: additionalUsers,
        restartKafka: productionSetupOptions.restartKafka
      });
      
      if (res.success) {
        setProductionSetupLogs(res.logs || []);
        if (res.adminPassword) {
          setGeneratedPassword(res.adminPassword);
        }
        setSuccess('Production security setup completed successfully!');
        setTimeout(() => {
          loadSecurityStatus();
          loadAclStatus();
        }, 2000);
      } else {
        setError(res.message);
        setProductionSetupLogs(res.logs || []);
      }
    } catch (err) {
      setError('Failed to setup production security: ' + err.message);
    } finally {
      setSettingUpProduction(false);
      setTimeout(() => setSuccess(null), 5000);
    }
  };

  // Handle Update Credentials
  const handleUpdateCredentials = async () => {
    if (!manualCredentials.username || !manualCredentials.password) {
      setError('Username and password are required');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const res = await apiService.updateSecurityCredentials(clusterId, manualCredentials);
      if (res.success) {
        setSuccess(res.message);
        setShowCredentialsDialog(false);
        setManualCredentials({ username: '', password: '', mechanism: 'SCRAM-SHA-512' });
        loadSecurityStatus();
      } else {
        setError(res.message || 'Failed to update credentials');
      }
    } catch (err) {
      setError('Failed to update credentials: ' + err.message);
    } finally {
      setLoading(false);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  // Handle Remove Credentials
  const handleRemoveCredentials = async () => {
    if (!window.confirm('Are you sure you want to remove SASL credentials? The backend will connect as ANONYMOUS.')) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const res = await apiService.removeSecurityCredentials(clusterId);
      if (res.success) {
        setSuccess(res.message);
        loadSecurityStatus();
      } else {
        setError(res.message || 'Failed to remove credentials');
      }
    } catch (err) {
      setError('Failed to remove credentials: ' + err.message);
    } finally {
      setLoading(false);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copied to clipboard!');
    setTimeout(() => setSuccess(null), 2000);
  };

  useEffect(() => {
    loadAclStatus();
    loadSecurityStatus();
  }, [loadAclStatus, loadSecurityStatus]);

  useEffect(() => {
    if (activeTab === 'acls') {
      loadACLs();
    } else if (activeTab === 'users') {
      loadUsers();
    } else if (activeTab === 'quotas') {
      loadQuotas();
    }
  }, [activeTab, loadACLs, loadUsers, loadQuotas]);

  const handleEnableACL = async () => {
    setEnabling(true);
    setEnableLogs([]);
    setError(null);
    
    try {
      const superUsers = enableOptions.superUsers
        .split(',')
        .map(u => u.trim())
        .filter(u => u);
      
      const res = await apiService.enableACL(clusterId, credentials, {
        superUsers,
        allowEveryoneIfNoAcl: enableOptions.allowEveryoneIfNoAcl,
        restartKafka: enableOptions.restartKafka
      });
      
      if (res.success) {
        setEnableLogs(res.logs || []);
        setSuccess('ACL authorization enabled successfully');
        setTimeout(() => {
          loadAclStatus();
          setShowEnableDialog(false);
        }, 2000);
      } else {
        setError(res.message);
      }
    } catch (err) {
      setError('Failed to enable ACL: ' + err.message);
    } finally {
      setEnabling(false);
      setTimeout(() => setSuccess(null), 5000);
    }
  };

  const handleDisableACL = async () => {
    if (!window.confirm('Are you sure you want to disable ACL authorization? This will allow unrestricted access to Kafka.')) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const res = await apiService.disableACL(clusterId, credentials, true);
      if (res.success) {
        setSuccess('ACL authorization disabled. Kafka is restarting...');
        setTimeout(() => {
          loadAclStatus();
        }, 5000);
      } else {
        setError(res.message);
      }
    } catch (err) {
      setError('Failed to disable ACL: ' + err.message);
    } finally {
      setLoading(false);
      setTimeout(() => setSuccess(null), 5000);
    }
  };

  const handleAddACL = async () => {
    if (!newACL.principal || !newACL.resource) {
      setError('Principal and resource are required');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.addACL(clusterId, credentials, newACL);
      if (res.success) {
        setSuccess('ACL added successfully');
        setShowAddACL(false);
        setNewACL({
          principal: 'User:',
          operation: 'All',
          resource: '',
          resourceType: 'topic',
          resourcePattern: 'literal',
          host: '*'
        });
        loadACLs();
      } else {
        setError(res.message || 'Failed to add ACL');
      }
    } catch (err) {
      setError('Failed to add ACL: ' + err.message);
    } finally {
      setLoading(false);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const handleRemoveACL = async (acl) => {
    if (!window.confirm(`Remove ACL for ${acl.principal} on ${acl.resourceType}:${acl.name}?`)) return;
    
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.removeACL(clusterId, credentials, {
        principal: acl.principal,
        operation: acl.operation,
        resource: acl.name,
        resourceType: acl.resourceType.toLowerCase(),
        resourcePattern: acl.patternType.toLowerCase(),
        host: acl.host
      });
      if (res.success) {
        setSuccess('ACL removed successfully');
        loadACLs();
      } else {
        setError(res.message || 'Failed to remove ACL');
      }
    } catch (err) {
      setError('Failed to remove ACL: ' + err.message);
    } finally {
      setLoading(false);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const handleAddUser = async () => {
    if (!newUser.username || !newUser.password) {
      setError('Username and password are required');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.createUser(
        clusterId, 
        credentials, 
        newUser.username, 
        newUser.password, 
        newUser.mechanism
      );
      if (res.success) {
        setSuccess(`User ${newUser.username} created successfully`);
        setShowAddUser(false);
        setNewUser({ username: '', password: '', mechanism: 'SCRAM-SHA-512' });
        loadUsers();
      } else {
        setError(res.message || 'Failed to create user');
      }
    } catch (err) {
      setError('Failed to create user: ' + err.message);
    } finally {
      setLoading(false);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const handleDeleteUser = async (username) => {
    if (!window.confirm(`Delete user ${username}?`)) return;
    
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.deleteUser(clusterId, credentials, username);
      if (res.success) {
        setSuccess(`User ${username} deleted successfully`);
        loadUsers();
      } else {
        setError(res.message || 'Failed to delete user');
      }
    } catch (err) {
      setError('Failed to delete user: ' + err.message);
    } finally {
      setLoading(false);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const handleAddQuota = async () => {
    if (!newQuota.username && !newQuota.clientId) {
      setError('Username or Client ID is required');
      return;
    }
    
    if (!newQuota.producerByteRate && !newQuota.consumerByteRate && !newQuota.requestPercentage) {
      setError('At least one quota value is required');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.setQuota(clusterId, credentials, {
        username: newQuota.username || undefined,
        clientId: newQuota.clientId || undefined,
        producerByteRate: newQuota.producerByteRate ? parseInt(newQuota.producerByteRate) : undefined,
        consumerByteRate: newQuota.consumerByteRate ? parseInt(newQuota.consumerByteRate) : undefined,
        requestPercentage: newQuota.requestPercentage ? parseFloat(newQuota.requestPercentage) : undefined
      });
      if (res.success) {
        setSuccess('Quota set successfully');
        setShowAddQuota(false);
        setNewQuota({
          username: '',
          clientId: '',
          producerByteRate: '',
          consumerByteRate: '',
          requestPercentage: ''
        });
        loadQuotas();
      } else {
        setError(res.message || 'Failed to set quota');
      }
    } catch (err) {
      setError('Failed to set quota: ' + err.message);
    } finally {
      setLoading(false);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const handleDeleteQuota = async (quota) => {
    if (!window.confirm(`Delete quota for ${quota.name}?`)) return;
    
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.deleteQuota(
        clusterId, 
        credentials, 
        quota.type === 'user' ? quota.name : undefined,
        quota.type === 'client' ? quota.name : undefined
      );
      if (res.success) {
        setSuccess('Quota deleted successfully');
        loadQuotas();
      } else {
        setError(res.message || 'Failed to delete quota');
      }
    } catch (err) {
      setError('Failed to delete quota: ' + err.message);
    } finally {
      setLoading(false);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  if (!credentials?.username) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Lock className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">SSH Credentials Required</h3>
          <p className="text-sm text-muted-foreground">
            Please configure SSH credentials to manage ACLs and users.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      {success && (
        <Alert className="border-green-500/50 bg-green-500/10">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-600">{success}</AlertDescription>
        </Alert>
      )}
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex justify-between items-center mb-4">
          <TabsList>
            <TabsTrigger value="security">
              <ShieldCheck className="h-4 w-4 mr-2" />Production Security
            </TabsTrigger>
            <TabsTrigger value="config">
              <Settings className="h-4 w-4 mr-2" />ACL Config
            </TabsTrigger>
            <TabsTrigger value="acls">
              <Key className="h-4 w-4 mr-2" />ACLs ({acls.length})
            </TabsTrigger>
            <TabsTrigger value="users">
              <Users className="h-4 w-4 mr-2" />Users ({users.length})
            </TabsTrigger>
            <TabsTrigger value="quotas">
              <Gauge className="h-4 w-4 mr-2" />Quotas ({quotas.length})
            </TabsTrigger>
          </TabsList>
        </div>
        
        {/* Production Security Tab */}
        <TabsContent value="security" className="mt-0">
          <div className="space-y-4">
            {/* Security Status Card */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5" />
                    Production Security Setup
                  </CardTitle>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={loadSecurityStatus}
                    disabled={securityStatusLoading}
                  >
                    <RefreshCw className={`h-3 w-3 mr-1 ${securityStatusLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Current Status */}
                  <div className="p-4 border rounded-lg bg-muted/30">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      Current Security Status
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-3 bg-background rounded border">
                        <p className="text-xs text-muted-foreground mb-1">SSL/TLS</p>
                        <Badge variant={securityStatus?.ssl ? 'success' : 'secondary'}>
                          {securityStatus?.ssl ? 'Enabled' : 'Disabled'}
                        </Badge>
                      </div>
                      <div className="p-3 bg-background rounded border">
                        <p className="text-xs text-muted-foreground mb-1">SASL Authentication</p>
                        <Badge variant={securityStatus?.saslEnabled ? 'success' : 'secondary'}>
                          {securityStatus?.saslEnabled ? 'Enabled' : 'Disabled'}
                        </Badge>
                      </div>
                      <div className="p-3 bg-background rounded border">
                        <p className="text-xs text-muted-foreground mb-1">Security Protocol</p>
                        <span className="text-sm font-mono">{securityStatus?.securityProtocol || 'N/A'}</span>
                      </div>
                      <div className="p-3 bg-background rounded border">
                        <p className="text-xs text-muted-foreground mb-1">Backend User</p>
                        <span className="text-sm font-mono">{securityStatus?.saslUser || 'ANONYMOUS'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Setup Options */}
                  {!securityStatus?.saslEnabled ? (
                    <div className="p-4 border rounded-lg border-primary/50 bg-primary/5">
                      <div className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                          <Rocket className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold">Enable Production Security</h4>
                          <p className="text-sm text-muted-foreground mt-1">
                            Set up SASL/SCRAM authentication with a superadmin user. This will:
                          </p>
                          <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                            <li className="flex items-center gap-2">
                              <CheckCircle2 className="h-3 w-3 text-green-500" />
                              Create a superadmin user with SCRAM-SHA-512
                            </li>
                            <li className="flex items-center gap-2">
                              <CheckCircle2 className="h-3 w-3 text-green-500" />
                              Enable ACL authorization on all brokers
                            </li>
                            <li className="flex items-center gap-2">
                              <CheckCircle2 className="h-3 w-3 text-green-500" />
                              Configure backend to authenticate automatically
                            </li>
                            <li className="flex items-center gap-2">
                              <CheckCircle2 className="h-3 w-3 text-green-500" />
                              Perform rolling restart of the cluster
                            </li>
                          </ul>
                          <div className="mt-4">
                            <Button onClick={() => setShowProductionSetup(true)}>
                              <ShieldCheck className="h-4 w-4 mr-2" />
                              Setup Production Security
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 border rounded-lg border-green-500/50 bg-green-500/5">
                      <div className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                          <CheckCircle2 className="h-5 w-5 text-green-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-green-600">Production Security Active</h4>
                          <p className="text-sm text-muted-foreground mt-1">
                            Your cluster is secured with SASL authentication. The backend is connecting as <strong>{securityStatus?.saslUser}</strong> using {securityStatus?.saslMechanism}.
                          </p>
                          <div className="mt-4 flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => setShowCredentialsDialog(true)}>
                              <Key className="h-3 w-3 mr-1" />
                              Update Credentials
                            </Button>
                            <Button variant="outline" size="sm" onClick={handleRemoveCredentials}>
                              <Trash2 className="h-3 w-3 mr-1" />
                              Remove Credentials
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Warning for non-production */}
                  {!securityStatus?.saslEnabled && aclStatus?.enabled && aclStatus?.allowEveryoneIfNoAcl && (
                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        <strong>Warning:</strong> ACL is enabled but <code>allow.everyone.if.no.acl.found=true</code>. 
                        This means anonymous users can still access resources without ACLs. For production, use the setup above.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        {/* Configuration Tab */}
        <TabsContent value="config" className="mt-0">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  ACL Authorization
                </CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={loadAclStatus}
                  disabled={aclStatusLoading}
                >
                  <RefreshCw className={`h-3 w-3 mr-1 ${aclStatusLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {aclStatusLoading && !aclStatus ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  <span className="ml-2">Checking ACL status...</span>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      {aclStatus?.enabled ? (
                        <div className="h-10 w-10 rounded-full bg-green-500/20 flex items-center justify-center">
                          <CheckCircle2 className="h-5 w-5 text-green-600" />
                        </div>
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                          <XCircle className="h-5 w-5 text-yellow-600" />
                        </div>
                      )}
                      <div>
                        <h4 className="font-semibold">
                          ACL Authorization is {aclStatus?.enabled ? 'Enabled' : 'Disabled'}
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          {aclStatus?.enabled 
                            ? 'Access control is active. Only authorized users can access resources.'
                            : 'Access control is not active. All users have unrestricted access.'}
                        </p>
                      </div>
                    </div>
                    <div>
                      {aclStatus?.enabled ? (
                        <Button 
                          variant="outline" 
                          onClick={handleDisableACL}
                          disabled={loading}
                        >
                          <PowerOff className="h-4 w-4 mr-2" />
                          Disable ACL
                        </Button>
                      ) : (
                        <Button 
                          onClick={() => setShowEnableDialog(true)}
                          disabled={loading}
                        >
                          <Power className="h-4 w-4 mr-2" />
                          Enable ACL
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {aclStatus?.enabled && (
                    <div className="grid grid-cols-3 gap-4 mt-4">
                      <div className="p-3 border rounded-lg">
                        <p className="text-xs text-muted-foreground mb-1">Authorizer</p>
                        <p className="text-sm font-mono truncate" title={aclStatus?.authorizer}>
                          {aclStatus?.authorizer?.split('.').pop() || 'N/A'}
                        </p>
                      </div>
                      <div className="p-3 border rounded-lg">
                        <p className="text-xs text-muted-foreground mb-1">Super Users</p>
                        <p className="text-sm font-mono truncate" title={aclStatus?.superUsers}>
                          {aclStatus?.superUsers || 'None'}
                        </p>
                      </div>
                      <div className="p-3 border rounded-lg">
                        <p className="text-xs text-muted-foreground mb-1">Allow If No ACL</p>
                        <Badge variant={aclStatus?.allowEveryoneIfNoAcl ? 'success' : 'destructive'}>
                          {aclStatus?.allowEveryoneIfNoAcl ? 'Yes' : 'No'}
                        </Badge>
                      </div>
                    </div>
                  )}
                  
                  {!aclStatus?.enabled && (
                    <Alert>
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        <strong>Security Notice:</strong> Without ACL authorization, all clients have unrestricted access to your Kafka cluster. 
                        Enable ACL to control who can read, write, or manage topics.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* ACLs Tab */}
        <TabsContent value="acls" className="mt-0">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-muted-foreground">
                Manage access control lists for Kafka resources
              </p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={loadACLs} disabled={loading}>
                  <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button size="sm" onClick={() => setShowAddACL(true)} disabled={!aclStatus?.enabled}>
                  <Plus className="h-3 w-3 mr-1" />Add ACL
                </Button>
              </div>
            </div>
            
            {!aclStatus?.enabled && (
              <Alert className="border-amber-500/50 bg-amber-500/10">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription>
                  <div className="space-y-2">
                    <p className="font-medium text-amber-600">ACL Authorization is Not Enabled</p>
                    <p className="text-sm">
                      The Kafka brokers don't have an Authorizer configured. To use ACLs, you need to:
                    </p>
                    <ol className="text-sm list-decimal list-inside space-y-1 ml-2">
                      <li>Go to the <strong>ACL Config</strong> tab and click "Enable ACL"</li>
                      <li>Or use the <strong>Production Security</strong> tab for a complete SASL + ACL setup</li>
                    </ol>
                    <p className="text-xs text-muted-foreground mt-2">
                      Note: Enabling ACL requires adding <code className="bg-muted px-1 rounded">authorizer.class.name</code> to server.properties and restarting Kafka.
                    </p>
                  </div>
                </AlertDescription>
              </Alert>
            )}
            
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-muted/50 px-3 py-2 grid grid-cols-12 gap-2 text-xs font-semibold border-b">
                <div className="col-span-3">Resource</div>
                <div className="col-span-3">Principal</div>
                <div className="col-span-2">Operation</div>
                <div className="col-span-2">Host</div>
                <div className="col-span-1">Permission</div>
                <div className="col-span-1 text-right">Actions</div>
              </div>
              <div className="max-h-[300px] overflow-y-auto">
                {acls.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    {loading ? 'Loading ACLs...' : 'No ACLs found.'}
                  </div>
                ) : (
                  acls.map((acl, i) => (
                    <div key={i} className="px-3 py-2 grid grid-cols-12 gap-2 text-xs border-b border-border/50 hover:bg-muted/30 items-center">
                      <div className="col-span-3">
                        <Badge variant="outline" className="mr-1 text-[10px]">{acl.resourceType}</Badge>
                        <span className="font-mono">{acl.name}</span>
                        {acl.patternType !== 'LITERAL' && (
                          <Badge variant="secondary" className="ml-1 text-[10px]">{acl.patternType}</Badge>
                        )}
                      </div>
                      <div className="col-span-3 font-mono truncate">{acl.principal}</div>
                      <div className="col-span-2">
                        <Badge variant="outline" className="text-[10px]">{acl.operation}</Badge>
                      </div>
                      <div className="col-span-2 font-mono">{acl.host}</div>
                      <div className="col-span-1">
                        <Badge variant={acl.permissionType === 'ALLOW' ? 'success' : 'destructive'} className="text-[10px]">
                          {acl.permissionType}
                        </Badge>
                      </div>
                      <div className="col-span-1 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-destructive"
                          onClick={() => handleRemoveACL(acl)}
                          disabled={loading}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </TabsContent>
        
        {/* Users Tab */}
        <TabsContent value="users" className="mt-0">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-muted-foreground">
                Manage SCRAM authentication users
              </p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={loadUsers} disabled={loading}>
                  <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button size="sm" onClick={() => setShowAddUser(true)}>
                  <UserPlus className="h-3 w-3 mr-1" />Add User
                </Button>
              </div>
            </div>
            
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-muted/50 px-3 py-2 grid grid-cols-12 gap-2 text-xs font-semibold border-b">
                <div className="col-span-5">Username</div>
                <div className="col-span-5">Authentication Mechanisms</div>
                <div className="col-span-2 text-right">Actions</div>
              </div>
              <div className="max-h-[300px] overflow-y-auto">
                {users.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    {loading ? 'Loading users...' : 'No SCRAM users found.'}
                  </div>
                ) : (
                  users.map((user, i) => (
                    <div key={i} className="px-3 py-2 grid grid-cols-12 gap-2 text-xs border-b border-border/50 hover:bg-muted/30 items-center">
                      <div className="col-span-5 font-mono font-medium">{user.username}</div>
                      <div className="col-span-5">
                        {user.mechanisms?.map((m, j) => (
                          <Badge key={j} variant="outline" className="mr-1 text-[10px]">{m}</Badge>
                        ))}
                      </div>
                      <div className="col-span-2 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 text-xs text-destructive"
                          onClick={() => handleDeleteUser(user.username)}
                          disabled={loading}
                        >
                          <Trash2 className="h-3 w-3 mr-1" />Delete
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </TabsContent>
        
        {/* Quotas Tab */}
        <TabsContent value="quotas" className="mt-0">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-muted-foreground">
                Manage rate limits for users and clients
              </p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={loadQuotas} disabled={loading}>
                  <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button size="sm" onClick={() => setShowAddQuota(true)}>
                  <Plus className="h-3 w-3 mr-1" />Set Quota
                </Button>
              </div>
            </div>
            
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-muted/50 px-3 py-2 grid grid-cols-12 gap-2 text-xs font-semibold border-b">
                <div className="col-span-2">Type</div>
                <div className="col-span-3">Name</div>
                <div className="col-span-2">Producer Rate</div>
                <div className="col-span-2">Consumer Rate</div>
                <div className="col-span-2">Request %</div>
                <div className="col-span-1 text-right">Actions</div>
              </div>
              <div className="max-h-[300px] overflow-y-auto">
                {quotas.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    {loading ? 'Loading quotas...' : 'No quotas configured.'}
                  </div>
                ) : (
                  quotas.map((quota, i) => (
                    <div key={i} className="px-3 py-2 grid grid-cols-12 gap-2 text-xs border-b border-border/50 hover:bg-muted/30 items-center">
                      <div className="col-span-2">
                        <Badge variant="outline" className="text-[10px]">{quota.type}</Badge>
                      </div>
                      <div className="col-span-3 font-mono font-medium">{quota.name}</div>
                      <div className="col-span-2 font-mono">
                        {quota.quotas?.producerByteRate 
                          ? `${(quota.quotas.producerByteRate / 1024 / 1024).toFixed(1)} MB/s`
                          : '-'}
                      </div>
                      <div className="col-span-2 font-mono">
                        {quota.quotas?.consumerByteRate 
                          ? `${(quota.quotas.consumerByteRate / 1024 / 1024).toFixed(1)} MB/s`
                          : '-'}
                      </div>
                      <div className="col-span-2 font-mono">
                        {quota.quotas?.requestPercentage 
                          ? `${quota.quotas.requestPercentage}%`
                          : '-'}
                      </div>
                      <div className="col-span-1 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-destructive"
                          onClick={() => handleDeleteQuota(quota)}
                          disabled={loading}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Enable ACL Dialog */}
      <Dialog open={showEnableDialog} onOpenChange={setShowEnableDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-4 w-4" />Enable ACL Authorization
            </DialogTitle>
            <DialogDescription>
              Configure and enable ACL authorization on all cluster nodes.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <div>
              <Label>Super Users (comma-separated)</Label>
              <Input 
                value={enableOptions.superUsers} 
                onChange={e => setEnableOptions({ ...enableOptions, superUsers: e.target.value })}
                placeholder="admin, service-account"
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Users that bypass all ACL checks. {enableOptions.allowEveryoneIfNoAcl 
                  ? 'ANONYMOUS will be included as a super user.' 
                  : 'ANONYMOUS will NOT be included (recommended for security).'}
              </p>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label>Allow Everyone If No ACL</Label>
                <p className="text-xs text-muted-foreground">
                  Allow operations when no ACL is defined for a resource
                </p>
              </div>
              <Switch 
                checked={enableOptions.allowEveryoneIfNoAcl}
                onCheckedChange={v => setEnableOptions({ ...enableOptions, allowEveryoneIfNoAcl: v })}
              />
            </div>
            
            {!enableOptions.allowEveryoneIfNoAcl && !enableOptions.superUsers.trim() && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Warning:</strong> With "Allow Everyone If No ACL" disabled and no super users specified,
                  no one (including the backend) will be able to manage ACLs or access resources without explicit ACLs.
                  You should specify at least one super user.
                </AlertDescription>
              </Alert>
            )}
            
            <div className="flex items-center justify-between">
              <div>
                <Label>Restart Kafka Automatically</Label>
                <p className="text-xs text-muted-foreground">
                  Restart Kafka on all nodes after configuration
                </p>
              </div>
              <Switch 
                checked={enableOptions.restartKafka}
                onCheckedChange={v => setEnableOptions({ ...enableOptions, restartKafka: v })}
              />
            </div>
            
            {enableLogs.length > 0 && (
              <div>
                <Label>Progress</Label>
                <ScrollArea className="h-32 border rounded p-2 bg-muted/50 mt-1">
                  {enableLogs.map((log, i) => (
                    <p key={i} className="text-xs font-mono">{log}</p>
                  ))}
                </ScrollArea>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEnableDialog(false)} disabled={enabling}>
              Cancel
            </Button>
            <Button onClick={handleEnableACL} disabled={enabling}>
              {enabling ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Enabling...
                </>
              ) : (
                <>
                  <Power className="h-4 w-4 mr-2" />
                  Enable ACL
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add ACL Dialog */}
      <Dialog open={showAddACL} onOpenChange={setShowAddACL}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-4 w-4" />Add ACL
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <div>
              <Label>Principal</Label>
              <Input 
                value={newACL.principal} 
                onChange={e => setNewACL({ ...newACL, principal: e.target.value })}
                placeholder="User:admin"
                className="mt-1 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">Format: User:username or Group:groupname</p>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Resource Type</Label>
                <Select value={newACL.resourceType} onValueChange={v => setNewACL({ ...newACL, resourceType: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="topic">Topic</SelectItem>
                    <SelectItem value="group">Consumer Group</SelectItem>
                    <SelectItem value="cluster">Cluster</SelectItem>
                    <SelectItem value="transactional-id">Transactional ID</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Pattern Type</Label>
                <Select value={newACL.resourcePattern} onValueChange={v => setNewACL({ ...newACL, resourcePattern: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="literal">Literal</SelectItem>
                    <SelectItem value="prefixed">Prefixed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Resource Name</Label>
              <Input 
                value={newACL.resource} 
                onChange={e => setNewACL({ ...newACL, resource: e.target.value })}
                placeholder="topic-name or * for all"
                className="mt-1 font-mono"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Operation</Label>
                <Select value={newACL.operation} onValueChange={v => setNewACL({ ...newACL, operation: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="All">All</SelectItem>
                    <SelectItem value="Read">Read</SelectItem>
                    <SelectItem value="Write">Write</SelectItem>
                    <SelectItem value="Create">Create</SelectItem>
                    <SelectItem value="Delete">Delete</SelectItem>
                    <SelectItem value="Alter">Alter</SelectItem>
                    <SelectItem value="Describe">Describe</SelectItem>
                    <SelectItem value="ClusterAction">Cluster Action</SelectItem>
                    <SelectItem value="DescribeConfigs">Describe Configs</SelectItem>
                    <SelectItem value="AlterConfigs">Alter Configs</SelectItem>
                    <SelectItem value="IdempotentWrite">Idempotent Write</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Host</Label>
                <Input 
                  value={newACL.host} 
                  onChange={e => setNewACL({ ...newACL, host: e.target.value })}
                  placeholder="* for all hosts"
                  className="mt-1 font-mono"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddACL(false)}>Cancel</Button>
            <Button onClick={handleAddACL} disabled={loading || !newACL.principal || !newACL.resource}>
              {loading ? 'Adding...' : 'Add ACL'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-4 w-4" />Create User
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <div>
              <Label>Username</Label>
              <Input 
                value={newUser.username} 
                onChange={e => setNewUser({ ...newUser, username: e.target.value })}
                placeholder="username"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Password</Label>
              <Input 
                type="password"
                value={newUser.password} 
                onChange={e => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="password"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Authentication Mechanism</Label>
              <Select value={newUser.mechanism} onValueChange={v => setNewUser({ ...newUser, mechanism: v })}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SCRAM-SHA-512">SCRAM-SHA-512</SelectItem>
                  <SelectItem value="SCRAM-SHA-256">SCRAM-SHA-256</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddUser(false)}>Cancel</Button>
            <Button onClick={handleAddUser} disabled={loading || !newUser.username || !newUser.password}>
              {loading ? 'Creating...' : 'Create User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Quota Dialog */}
      <Dialog open={showAddQuota} onOpenChange={setShowAddQuota}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gauge className="h-4 w-4" />Set Quota
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Username</Label>
                <Input 
                  value={newQuota.username} 
                  onChange={e => setNewQuota({ ...newQuota, username: e.target.value })}
                  placeholder="Optional"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Client ID</Label>
                <Input 
                  value={newQuota.clientId} 
                  onChange={e => setNewQuota({ ...newQuota, clientId: e.target.value })}
                  placeholder="Optional"
                  className="mt-1"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Specify a username, client ID, or both to set quotas for specific entities.
            </p>
            
            <div>
              <Label>Producer Byte Rate (bytes/sec)</Label>
              <Input 
                type="number"
                value={newQuota.producerByteRate} 
                onChange={e => setNewQuota({ ...newQuota, producerByteRate: e.target.value })}
                placeholder="e.g., 1048576 (1 MB/s)"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Consumer Byte Rate (bytes/sec)</Label>
              <Input 
                type="number"
                value={newQuota.consumerByteRate} 
                onChange={e => setNewQuota({ ...newQuota, consumerByteRate: e.target.value })}
                placeholder="e.g., 2097152 (2 MB/s)"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Request Percentage (%)</Label>
              <Input 
                type="number"
                value={newQuota.requestPercentage} 
                onChange={e => setNewQuota({ ...newQuota, requestPercentage: e.target.value })}
                placeholder="e.g., 50"
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Maximum CPU time percentage for request processing.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddQuota(false)}>Cancel</Button>
            <Button 
              onClick={handleAddQuota} 
              disabled={loading || (!newQuota.username && !newQuota.clientId) || (!newQuota.producerByteRate && !newQuota.consumerByteRate && !newQuota.requestPercentage)}
            >
              {loading ? 'Setting...' : 'Set Quota'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Production Security Setup Dialog */}
      <Dialog open={showProductionSetup} onOpenChange={(open) => {
        if (!settingUpProduction) {
          setShowProductionSetup(open);
          if (!open) {
            setProductionSetupLogs([]);
            setGeneratedPassword(null);
          }
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5" />
              Production Security Setup
            </DialogTitle>
            <DialogDescription>
              Configure SASL/SCRAM authentication for your Kafka cluster.
            </DialogDescription>
          </DialogHeader>
          
          {!generatedPassword ? (
            <div className="space-y-4 py-3">
              <div>
                <Label>Admin Username</Label>
                <Input 
                  value={productionSetupOptions.adminUsername} 
                  onChange={e => setProductionSetupOptions({ ...productionSetupOptions, adminUsername: e.target.value })}
                  placeholder="superadmin"
                  className="mt-1 font-mono"
                  disabled={settingUpProduction}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Auto-generate Password</Label>
                  <p className="text-xs text-muted-foreground">
                    Generate a secure random password
                  </p>
                </div>
                <Switch 
                  checked={productionSetupOptions.useGeneratedPassword}
                  onCheckedChange={v => setProductionSetupOptions({ ...productionSetupOptions, useGeneratedPassword: v })}
                  disabled={settingUpProduction}
                />
              </div>
              
              {!productionSetupOptions.useGeneratedPassword && (
                <div>
                  <Label>Admin Password</Label>
                  <Input 
                    type="password"
                    value={productionSetupOptions.adminPassword} 
                    onChange={e => setProductionSetupOptions({ ...productionSetupOptions, adminPassword: e.target.value })}
                    placeholder="Enter password"
                    className="mt-1"
                    disabled={settingUpProduction}
                  />
                </div>
              )}
              
              <div>
                <Label>Authentication Mechanism</Label>
                <Select 
                  value={productionSetupOptions.mechanism} 
                  onValueChange={v => setProductionSetupOptions({ ...productionSetupOptions, mechanism: v })}
                  disabled={settingUpProduction}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SCRAM-SHA-512">SCRAM-SHA-512 (Recommended)</SelectItem>
                    <SelectItem value="SCRAM-SHA-256">SCRAM-SHA-256</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Additional Super Users (comma-separated)</Label>
                <Input 
                  value={productionSetupOptions.additionalSuperUsers} 
                  onChange={e => setProductionSetupOptions({ ...productionSetupOptions, additionalSuperUsers: e.target.value })}
                  placeholder="admin2, service-account"
                  className="mt-1"
                  disabled={settingUpProduction}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Allow Everyone If No ACL</Label>
                  <p className="text-xs text-muted-foreground">
                    Should be OFF for production security
                  </p>
                </div>
                <Switch 
                  checked={productionSetupOptions.allowEveryoneIfNoAcl}
                  onCheckedChange={v => setProductionSetupOptions({ ...productionSetupOptions, allowEveryoneIfNoAcl: v })}
                  disabled={settingUpProduction}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Rolling Restart Cluster</Label>
                  <p className="text-xs text-muted-foreground">
                    Restart Kafka on all nodes for changes to take effect
                  </p>
                </div>
                <Switch 
                  checked={productionSetupOptions.restartKafka}
                  onCheckedChange={v => setProductionSetupOptions({ ...productionSetupOptions, restartKafka: v })}
                  disabled={settingUpProduction}
                />
              </div>
              
              {settingUpProduction && (
                <div className="border rounded-lg p-4 bg-blue-500/5 border-blue-500/20">
                  <div className="flex items-center gap-3 mb-2">
                    <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                    <span className="font-medium text-blue-600">Setting up production security...</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    This process includes creating JAAS configuration, updating Kafka settings on all brokers, 
                    and performing a rolling restart. This typically takes 2-3 minutes.
                  </p>
                </div>
              )}
              
              {productionSetupLogs.length > 0 && (
                <div>
                  <Label>Progress</Label>
                  <ScrollArea className="h-40 border rounded p-2 bg-muted/50 mt-1">
                    {productionSetupLogs.map((log, i) => (
                      <p key={i} className="text-xs font-mono whitespace-pre-wrap">{log}</p>
                    ))}
                  </ScrollArea>
                </div>
              )}
              
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  This will modify Kafka configuration on all brokers and perform a rolling restart. 
                  Make sure you have a backup and understand the implications.
                </AlertDescription>
              </Alert>
            </div>
          ) : (
            <div className="space-y-4 py-3">
              <Alert className="border-green-500/50 bg-green-500/10">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-600">
                  Production security setup completed successfully!
                </AlertDescription>
              </Alert>
              
              <div className="p-4 border rounded-lg bg-muted/30">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Key className="h-4 w-4" />
                  Admin Credentials
                </h4>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Username</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="flex-1 p-2 bg-background rounded border font-mono text-sm">
                        {productionSetupOptions.adminUsername}
                      </code>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => copyToClipboard(productionSetupOptions.adminUsername)}
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Password</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="flex-1 p-2 bg-background rounded border font-mono text-sm">
                        {showPassword ? generatedPassword : '••••••••••••••••'}
                      </code>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => copyToClipboard(generatedPassword)}
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Important:</strong> Save these credentials securely! The password will not be shown again.
                  The backend is now configured to use these credentials automatically.
                </AlertDescription>
              </Alert>
              
              {productionSetupLogs.length > 0 && (
                <div>
                  <Label>Setup Log</Label>
                  <ScrollArea className="h-32 border rounded p-2 bg-muted/50 mt-1">
                    {productionSetupLogs.map((log, i) => (
                      <p key={i} className="text-xs font-mono whitespace-pre-wrap">{log}</p>
                    ))}
                  </ScrollArea>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            {!generatedPassword ? (
              <>
                <Button variant="outline" onClick={() => setShowProductionSetup(false)} disabled={settingUpProduction}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleProductionSecuritySetup} 
                  disabled={settingUpProduction || !productionSetupOptions.adminUsername || (!productionSetupOptions.useGeneratedPassword && !productionSetupOptions.adminPassword)}
                >
                  {settingUpProduction ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Setting Up... (this may take 2-3 minutes)
                    </>
                  ) : (
                    <>
                      <Rocket className="h-4 w-4 mr-2" />
                      Setup Production Security
                    </>
                  )}
                </Button>
              </>
            ) : (
              <Button onClick={() => {
                setShowProductionSetup(false);
                setGeneratedPassword(null);
                setProductionSetupLogs([]);
              }}>
                Done
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Credentials Dialog */}
      <Dialog open={showCredentialsDialog} onOpenChange={setShowCredentialsDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-4 w-4" />
              Update Backend Credentials
            </DialogTitle>
            <DialogDescription>
              Update the SASL credentials used by the backend to connect to Kafka.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <div>
              <Label>Username</Label>
              <Input 
                value={manualCredentials.username} 
                onChange={e => setManualCredentials({ ...manualCredentials, username: e.target.value })}
                placeholder="superadmin"
                className="mt-1 font-mono"
              />
            </div>
            <div>
              <Label>Password</Label>
              <Input 
                type="password"
                value={manualCredentials.password} 
                onChange={e => setManualCredentials({ ...manualCredentials, password: e.target.value })}
                placeholder="Enter password"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Mechanism</Label>
              <Select 
                value={manualCredentials.mechanism} 
                onValueChange={v => setManualCredentials({ ...manualCredentials, mechanism: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SCRAM-SHA-512">SCRAM-SHA-512</SelectItem>
                  <SelectItem value="SCRAM-SHA-256">SCRAM-SHA-256</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCredentialsDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleUpdateCredentials} 
              disabled={loading || !manualCredentials.username || !manualCredentials.password}
            >
              {loading ? 'Updating...' : 'Update Credentials'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ACLManager;
